'use client';

import React from 'react';
import CapabilityRadarChart from './CapabilityRadarChart';

const platformStats = [
  { value: '10,000+', label: 'Assessments' },
  { value: '150+', label: 'Enterprises' },
  { value: '6', label: 'Persona Journeys' },
  { value: '7', label: 'Transformation Phases' },
];

const testimonial = {
  quote:
    'KCF gave us a clear picture of where our AI capability actually stood. The heat map was a revelation — we thought we were at Level 3. We were at Level 1.',
  author: 'Rajiv Sharma',
  role: 'Chief Digital Officer',
  company: 'TechCorp Global',
  maturity: 'Now: Operational',
};

const phaseLabels = [
  { id: 'phase-assess', label: 'ASSESS', done: true },
  { id: 'phase-skill', label: 'SKILL', done: true },
  { id: 'phase-sandbox', label: 'SANDBOX', active: true },
  { id: 'phase-build', label: 'BUILD', done: false },
  { id: 'phase-scale', label: 'SCALE', done: false },
  { id: 'phase-productize', label: 'PRODUCTIZE', done: false },
  { id: 'phase-spinout', label: 'SPIN-OUT', done: false },
];

export default function BrandPanel() {
  return (
    <div className="w-full h-full flex flex-col relative overflow-hidden">
      {/* Gradient overlay — keeps text legible over the full-screen hero image rendered in AuthLayout */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(160deg, rgba(13,79,108,0.45) 0%, rgba(5,13,26,0.60) 40%, rgba(5,13,26,0.75) 100%)',
        }}
      />
      {/* Decorative circuit lines */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <svg width="100%" height="100%" viewBox="0 0 700 900" fill="none" preserveAspectRatio="xMidYMid slice">
          <line x1="0" y1="200" x2="700" y2="200" stroke="rgba(13,79,108,0.3)" strokeWidth="1" strokeDasharray="4 8" />
          <line x1="0" y1="600" x2="700" y2="600" stroke="rgba(201,168,76,0.15)" strokeWidth="1" strokeDasharray="4 8" />
          <line x1="350" y1="0" x2="350" y2="900" stroke="rgba(13,79,108,0.2)" strokeWidth="1" strokeDasharray="4 8" />
          <circle cx="350" cy="200" r="4" fill="rgba(201,168,76,0.4)" />
          <circle cx="350" cy="600" r="4" fill="rgba(13,79,108,0.6)" />
          <circle cx="100" cy="450" r="2" fill="rgba(201,168,76,0.3)" />
          <circle cx="600" cy="350" r="2" fill="rgba(13,79,108,0.5)" />
        </svg>
      </div>
      <div className="relative z-10 flex flex-col h-full p-8 xl:p-10 2xl:p-12">
        {/* Hero headline */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-slow" />
            <span className="text-2xs font-700 text-primary tracking-widest uppercase">
              Capability Intelligence OS
            </span>
          </div>
          <h1 className="text-4xl xl:text-5xl 2xl:text-6xl font-800 leading-tight mb-4">
            <span className="text-foreground">Measure. Build.</span>
            <br />
            <span className="text-gradient-gold">Transform.</span>
          </h1>
          <p className="text-base xl:text-md text-muted-foreground leading-relaxed max-w-md">
            Assess your AI & digital maturity across 14 dimensions. Discover gaps, unlock opportunities,
            and build the transformation roadmap your organisation needs.
          </p>
        </div>

        {/* Radar chart section */}
        <div className="glass-card rounded-xl p-4 mb-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-600 text-muted-foreground tracking-wider uppercase">
                KCF Intelligence Preview
              </p>
              <p className="text-sm font-700 text-foreground mt-0.5">
                TechCorp Global — AI Readiness Map
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xs text-muted-foreground">Overall Score</p>
              <p className="text-2xl font-800 text-gradient-gold tabular-nums">67</p>
              <span className="badge-maturity badge-developing">Developing</span>
            </div>
          </div>
          <CapabilityRadarChart />
          <div className="divider-gold mt-3 mb-3" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--gold-500)' }} />
              <span className="text-xs text-muted-foreground">Current</span>
              <span className="w-2 h-2 rounded-full ml-2" style={{ background: 'rgba(13,79,108,0.8)', border: '1px solid rgba(13,79,108,0.6)' }} />
              <span className="text-xs text-muted-foreground">Industry Avg</span>
            </div>
            <span className="text-xs font-600 text-emerald">
              +12pts since last assessment
            </span>
          </div>
        </div>

        {/* Transformation phase tracker */}
        <div className="mb-6">
          <p className="text-2xs font-600 text-muted-foreground tracking-widest uppercase mb-3">
            KCF Transformation Engine
          </p>
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {phaseLabels?.map((phase, idx) => (
              <React.Fragment key={phase?.id}>
                <div className="flex-shrink-0 flex flex-col items-center gap-1">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-2xs font-700 transition-all"
                    style={
                      phase?.done
                        ? { background: 'var(--teal-700)', border: '1px solid var(--teal-500)', color: '#4DD9FF' }
                        : phase?.active
                        ? { background: 'rgba(201,168,76,0.15)', border: '1px solid var(--gold-500)', color: 'var(--gold-500)' }
                        : { background: 'rgba(26,46,74,0.5)', border: '1px solid rgba(30,58,90,0.6)', color: 'var(--muted-foreground)' }
                    }
                  >
                    {phase?.done ? '✓' : idx + 1}
                  </div>
                  <span
                    className="text-2xs font-600 tracking-wider whitespace-nowrap"
                    style={
                      phase?.active
                        ? { color: 'var(--gold-500)' }
                        : phase?.done
                        ? { color: '#4DD9FF' }
                        : { color: 'var(--muted-foreground)' }
                    }
                  >
                    {phase?.label}
                  </span>
                </div>
                {idx < phaseLabels?.length - 1 && (
                  <div
                    className="flex-1 h-px min-w-[8px]"
                    style={{
                      background: phase?.done
                        ? 'rgba(13,79,108,0.6)'
                        : 'rgba(30,58,90,0.4)',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          {platformStats?.map((stat) => (
            <div key={`stat-${stat?.label}`} className="stat-card">
              <p className="text-lg xl:text-xl font-800 text-gradient-gold tabular-nums">{stat?.value}</p>
              <p className="text-2xs text-muted-foreground mt-0.5 leading-tight">{stat?.label}</p>
            </div>
          ))}
        </div>

        {/* Testimonial */}
        <div
          className="mt-auto rounded-xl p-4"
          style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.12)' }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-800 flex-shrink-0 mt-0.5"
              style={{ background: 'linear-gradient(135deg, var(--teal-700), var(--teal-500))', color: '#E0F0FF' }}
            >
              RS
            </div>
            <div>
              <p className="text-xs text-muted-foreground leading-relaxed italic mb-2">
                &ldquo;{testimonial?.quote}&rdquo;
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs font-700 text-foreground">{testimonial?.author}</span>
                <span className="text-2xs text-muted-foreground">·</span>
                <span className="text-2xs text-muted-foreground">{testimonial?.role}, {testimonial?.company}</span>
                <span className="badge-maturity badge-operational ml-1">{testimonial?.maturity}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}